import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ViewIcon } from "@chakra-ui/icons";
import { FiRefreshCw, FiPlay, FiCheckCircle } from "react-icons/fi";
import { sprintService, projectService } from "../services/edosService";
import api from "../services/api";

const STATUS_COLORS = {
  planned: "gray",
  active: "green",
  completed: "teal",
  cancelled: "red",
};

export default function SprintsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [projects, setProjects] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [projectTickets, setProjectTickets] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(
    searchParams.get("projectId") || "",
  );

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch (_) {}
  }, []);

  const fetchSprints = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    try {
      const res = await sprintService.listByProject(selectedProjectId);
      setSprints(res.data.data || res.data.sprints || []);
    } catch (err) {
      toast({ title: "Failed to load sprints", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, toast]);

  const normalizeTicketRows = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.tickets)) return payload.data.tickets;
    if (Array.isArray(payload?.tickets)) return payload.tickets;
    return [];
  };

  const fetchProjectTickets = useCallback(async () => {
    if (!selectedProjectId) {
      setProjectTickets([]);
      return;
    }
    setTicketsLoading(true);
    try {
      const allRows = [];
      let page = 1;
      let totalPages = 1;

      do {
        const res = await api.get("/api/tickets", {
          params: {
            page,
            limit: 100,
            projectId: selectedProjectId,
            _viewMode: "all",
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        });

        const payload = res?.data;
        allRows.push(...normalizeTicketRows(payload));
        totalPages = Number(
          payload?.totalPages || payload?.data?.totalPages || 1,
        );
        page += 1;
      } while (page <= totalPages && page <= 50);

      setProjectTickets(allRows);
    } catch {
      setProjectTickets([]);
      toast({ title: "Failed to load sprint tickets", status: "error" });
    } finally {
      setTicketsLoading(false);
    }
  }, [selectedProjectId, toast]);

  const ticketCountsBySprint = useMemo(() => {
    const counts = {};
    projectTickets.forEach((ticket) => {
      const sid = ticket?.sprintId?._id || ticket?.sprintId;
      if (!sid) return;
      counts[sid] = (counts[sid] || 0) + 1;
    });
    return counts;
  }, [projectTickets]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchSprints();
  }, [fetchSprints]);

  useEffect(() => {
    fetchProjectTickets();
  }, [fetchProjectTickets]);

  const handleStart = async (id) => {
    try {
      await sprintService.start(id);
      toast({ title: "Sprint started", status: "success" });
      fetchSprints();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to start sprint",
        status: "error",
      });
    }
  };

  const handleComplete = async (id) => {
    if (!window.confirm("Mark this sprint as completed?")) return;
    try {
      await sprintService.complete(id);
      toast({ title: "Sprint completed", status: "success" });
      fetchSprints();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to complete sprint",
        status: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sprint?")) return;
    try {
      await sprintService.remove(id);
      toast({ title: "Sprint deleted", status: "success" });
      fetchSprints();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to delete",
        status: "error",
      });
    }
  };

  return (
    <Box p={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Sprints
          </Text>
          <Text color="gray.500" fontSize="sm">
            Plan and track sprints per project
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={() => {
              fetchSprints();
              fetchProjectTickets();
            }}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="purple"
            onClick={() =>
              navigate(
                `/edos/sprints/create${selectedProjectId ? `?projectId=${selectedProjectId}` : ""}`,
              )
            }
          >
            New Sprint
          </Button>
        </HStack>
      </Flex>

      {/* Project filter */}
      <Flex gap={3} mb={4} align="center">
        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
          Select Project:
        </Text>
        <Select
          placeholder="Choose a project..."
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          maxW="320px"
        >
          {projects.map((p) => (
            <option key={p._id} value={p._id}>
              {p.projectName}
            </option>
          ))}
        </Select>
      </Flex>

      {!selectedProjectId ? (
        <Alert status="info">
          <AlertIcon />
          Select a project to view its sprints.
        </Alert>
      ) : (
        <Box
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          overflow="hidden"
        >
          {loading ? (
            <Flex justify="center" p={10}>
              <Spinner size="xl" color="purple.500" />
            </Flex>
          ) : sprints.length === 0 ? (
            <Alert status="info" m={4}>
              <AlertIcon />
              No sprints yet. Create the first sprint.
            </Alert>
          ) : (
            <Table variant="simple" size="sm">
              <Thead bg={headerBg}>
                <Tr>
                  <Th>Sprint</Th>
                  <Th>Status</Th>
                  <Th>Goal</Th>
                  <Th>Start</Th>
                  <Th>End</Th>
                  <Th>Points</Th>
                  <Th>Tickets</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sprints.map((s) => (
                  <Tr key={s._id}>
                    <Td>
                      <Text fontWeight="semibold" fontSize="sm">
                        {s.sprintName}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS[s.status] || "gray"}>
                        {s.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Text
                        fontSize="sm"
                        noOfLines={1}
                        maxW="160px"
                        color="gray.600"
                      >
                        {s.sprintGoal || "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {s.startDate
                          ? new Date(s.startDate).toLocaleDateString()
                          : "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {s.endDate
                          ? new Date(s.endDate).toLocaleDateString()
                          : "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{s.plannedStoryPoints ?? "-"}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {ticketCountsBySprint[s._id] || 0}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="View Analytics">
                          <IconButton
                            size="xs"
                            icon={<ViewIcon />}
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => navigate(`/edos/sprints/${s._id}`)}
                          />
                        </Tooltip>
                        {s.status === "planned" && (
                          <Tooltip label="Start Sprint">
                            <IconButton
                              size="xs"
                              icon={<FiPlay />}
                              variant="ghost"
                              colorScheme="green"
                              onClick={() => handleStart(s._id)}
                            />
                          </Tooltip>
                        )}
                        {s.status === "active" && (
                          <Tooltip label="Complete Sprint">
                            <IconButton
                              size="xs"
                              icon={<FiCheckCircle />}
                              variant="ghost"
                              colorScheme="teal"
                              onClick={() => handleComplete(s._id)}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="Delete">
                          <IconButton
                            size="xs"
                            icon={<DeleteIcon />}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(s._id)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
}
